import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  type PropsWithChildren,
} from "react";
import type { CurrentUser, PublicConfig } from "../../shared/types";
import { api, ApiError, jsonBody } from "../lib/api";

interface SessionContextValue {
  user: CurrentUser | null;
  config: PublicConfig | null;
  loading: boolean;
  login(input: { email: string; password: string }): Promise<CurrentUser>;
  register(input: { email: string; password: string }): Promise<CurrentUser>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: ["public-config"],
    queryFn: () => api<PublicConfig>("/api/public/config"),
    staleTime: 60_000,
  });
  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        return await api<CurrentUser>("/api/auth/session");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<CurrentUser>("/api/auth/login", {
        method: "POST",
        ...jsonBody(input),
      }),
    onSuccess(user) {
      queryClient.setQueryData(["session"], user);
    },
  });
  const registerMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<CurrentUser>("/api/auth/register", {
        method: "POST",
        ...jsonBody(input),
      }),
    onSuccess(user) {
      queryClient.setQueryData(["session"], user);
      void queryClient.invalidateQueries({ queryKey: ["public-config"] });
    },
  });

  const logout = useCallback(async () => {
    await api<{ loggedOut: boolean }>("/api/auth/logout", { method: "POST" });
    queryClient.setQueryData(["session"], null);
    queryClient.removeQueries({ queryKey: ["dashboard"] });
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["session"] }),
      queryClient.invalidateQueries({ queryKey: ["public-config"] }),
    ]);
  }, [queryClient]);

  return (
    <SessionContext.Provider
      value={{
        user: sessionQuery.data ?? null,
        config: configQuery.data ?? null,
        loading: sessionQuery.isLoading || configQuery.isLoading,
        login: loginMutation.mutateAsync,
        register: registerMutation.mutateAsync,
        logout,
        refresh,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}

